import React, { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Save, Building2, Landmark, Percent, Upload, Image as ImageIcon, Trash2, Mail } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function Settings() {
  const { readOnly } = useAuth();
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["settings"], queryFn: async () => (await api.get("/settings")).data });
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => { if (data) setForm(data); }, [data]);

  if (!form) return <div className="text-sm text-slate-400 py-14 text-center">Lädt…</div>;

  const set = (k) => (v) => setForm({ ...form, [k]: v });

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Bitte eine Bilddatei wählen.");
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data: res } = await api.post("/settings/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, logo_url: res.logo_url }));
      qc.invalidateQueries({ queryKey: ["settings"] });
      toast.success("Logo hochgeladen");
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/settings", {
        ...form,
        default_vat_rate: Number(form.default_vat_rate || 0),
        default_payment_days: Number(form.default_payment_days || 0),
        reminder_days: Number(form.reminder_days || 7),
      });
      qc.invalidateQueries({ queryKey: ["settings"] });
      toast.success("Firmendaten gespeichert");
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6 max-w-4xl" data-testid="settings-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Firmendaten</h1>
          <p className="text-sm text-slate-500 mt-1">Ihr Absender erscheint automatisch auf jeder Rechnung.</p>
        </div>
        <button onClick={save} disabled={busy || readOnly} data-testid="save-settings-button" className="bg-blue-600 hover:bg-blue-700 text-white font-medium px-4 py-2.5 rounded-lg inline-flex items-center gap-2 text-sm disabled:opacity-60">
          <Save className="h-4 w-4" /> {busy ? "Speichern…" : "Speichern"}
        </button>
      </div>

      <Section icon={Building2} title="Absender / Firma">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <F label="Firmenname" testid="set-company" value={form.company_name} onChange={set("company_name")} />
          <F label="Inhaber / Name" testid="set-owner" value={form.owner_name} onChange={set("owner_name")} />
          <F label="Straße & Nr." testid="set-street" value={form.street} onChange={set("street")} />
          <div className="grid grid-cols-3 gap-3">
            <F label="PLZ" testid="set-zip" value={form.zip} onChange={set("zip")} />
            <div className="col-span-2"><F label="Ort" testid="set-city" value={form.city} onChange={set("city")} /></div>
          </div>
          <F label="Telefon" testid="set-phone" value={form.phone} onChange={set("phone")} />
          <F label="E-Mail" testid="set-email" value={form.email} onChange={set("email")} />
          <F label="Webseite" testid="set-website" value={form.website} onChange={set("website")} />
        </div>

        <div className="mt-5 border-t border-slate-100 pt-5">
          <span className="block text-xs font-medium text-slate-600 mb-2">Firmenlogo (erscheint oben auf jeder Rechnung)</span>
          <div className="flex items-center gap-4 flex-wrap">
            <div className="h-20 w-40 rounded-lg border border-slate-200 bg-slate-50 grid place-items-center overflow-hidden" data-testid="logo-preview">
              {form.logo_url ? (
                <img src={form.logo_url} alt="Logo" className="max-h-full max-w-full object-contain" />
              ) : (
                <ImageIcon className="h-6 w-6 text-slate-300" />
              )}
            </div>
            <div className="flex items-center gap-2">
              <input ref={fileRef} type="file" accept="image/*" onChange={uploadLogo} className="hidden" data-testid="logo-file-input" />
              <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading || readOnly} data-testid="upload-logo-button" className="bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 font-medium px-4 py-2.5 rounded-lg inline-flex items-center gap-2 text-sm disabled:opacity-60">
                <Upload className="h-4 w-4" /> {uploading ? "Lädt hoch…" : form.logo_url ? "Logo ersetzen" : "Logo hochladen"}
              </button>
              {form.logo_url && (
                <button type="button" onClick={() => setForm({ ...form, logo_url: "" })} data-testid="remove-logo-button" className="h-9 w-9 grid place-items-center rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50">
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
          <p className="text-xs text-slate-400 mt-2">PNG, JPG, WEBP oder SVG · max. 5 MB. Speichern nicht vergessen.</p>
        </div>
      </Section>

      <Section icon={Landmark} title="Steuer & Bank">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <F label="Steuernummer" testid="set-taxnr" value={form.tax_number} onChange={set("tax_number")} />
          <F label="USt-IdNr." testid="set-vatid" value={form.vat_id} onChange={set("vat_id")} />
          <F label="Bankname" testid="set-bank" value={form.bank_name} onChange={set("bank_name")} />
          <F label="BIC" testid="set-bic" value={form.bic} onChange={set("bic")} />
          <div className="sm:col-span-2"><F label="IBAN" testid="set-iban" value={form.iban} onChange={set("iban")} /></div>
        </div>
      </Section>

      <Section icon={Percent} title="Rechnungs-Standards">
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
          <F label="Rechnungsnummer-Präfix" testid="set-prefix" value={form.invoice_prefix} onChange={set("invoice_prefix")} />
          <F label="Standard-MwSt (%)" type="number" testid="set-vatrate" value={form.default_vat_rate} onChange={set("default_vat_rate")} />
          <F label="Zahlungsziel (Tage)" type="number" testid="set-days" value={form.default_payment_days} onChange={set("default_payment_days")} />
          <F label="Mahnfrist (Tage)" type="number" testid="set-reminder-days" value={form.reminder_days} onChange={set("reminder_days")} />
        </div>
        <label className="flex items-center gap-3 mt-4 cursor-pointer">
          <input type="checkbox" checked={Boolean(form.kleinunternehmer)} onChange={(e) => setForm({ ...form, kleinunternehmer: e.target.checked })} data-testid="set-klein" className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500/30" />
          <span className="text-sm text-slate-700">Ich bin Kleinunternehmer (§ 19 UStG) – standardmäßig keine MwSt ausweisen</span>
        </label>
        <div className="mt-4">
          <span className="block text-xs font-medium text-slate-600 mb-1">Standard-Zahlungstext</span>
          <textarea value={form.default_note || ""} onChange={(e) => setForm({ ...form, default_note: e.target.value })} rows={2} data-testid="set-note" className="w-full bg-white border border-slate-200 rounded-lg px-3.5 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500" />
        </div>
      </Section>

      <Section icon={Mail} title="E-Mail-Vorlagen">
        <p className="text-xs text-slate-500 mb-4">Standardtexte für den Versand. Platzhalter: <code className="bg-slate-100 px-1 rounded">{"{number}"}</code> (Rechnungsnr.), <code className="bg-slate-100 px-1 rounded">{"{amount}"}</code> (Betrag), <code className="bg-slate-100 px-1 rounded">{"{title}"}</code> (Mahnstufe). Anrede und Grußformel werden automatisch ergänzt.</p>
        <div className="space-y-5">
          <div className="border border-slate-100 rounded-xl p-4">
            <span className="block text-xs font-semibold text-slate-600 mb-3">Rechnung</span>
            <F label="Betreff" testid="set-email-subject-invoice" value={form.email_subject_invoice} onChange={set("email_subject_invoice")} />
            <div className="mt-3">
              <span className="block text-xs font-medium text-slate-600 mb-1">Anschreiben</span>
              <textarea value={form.email_body_invoice || ""} onChange={(e) => setForm({ ...form, email_body_invoice: e.target.value })} rows={3} data-testid="set-email-body-invoice" className="w-full bg-white border border-slate-200 rounded-lg px-3.5 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500" />
            </div>
          </div>
          <div className="border border-slate-100 rounded-xl p-4">
            <span className="block text-xs font-semibold text-slate-600 mb-3">Mahnung / Zahlungserinnerung</span>
            <F label="Betreff" testid="set-email-subject-reminder" value={form.email_subject_reminder} onChange={set("email_subject_reminder")} />
            <div className="mt-3">
              <span className="block text-xs font-medium text-slate-600 mb-1">Anschreiben</span>
              <textarea value={form.email_body_reminder || ""} onChange={(e) => setForm({ ...form, email_body_reminder: e.target.value })} rows={3} data-testid="set-email-body-reminder" className="w-full bg-white border border-slate-200 rounded-lg px-3.5 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500" />
            </div>
          </div>
        </div>
      </Section>
    </div>
  );
}

function Section({ icon: Icon, title, children }) {
  return (
    <div className="bg-white border border-slate-200/80 rounded-2xl p-6">
      <h3 className="font-display font-semibold text-base mb-4 inline-flex items-center gap-2">
        <Icon className="h-4 w-4 text-slate-500" /> {title}
      </h3>
      {children}
    </div>
  );
}

function F({ label, testid, type = "text", value, onChange }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-slate-600 mb-1">{label}</span>
      <input type={type} data-testid={testid} value={value ?? ""} onChange={(e) => onChange(e.target.value)} step={type === "number" ? "any" : undefined} className="w-full bg-white border border-slate-200 rounded-lg px-3.5 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500" />
    </label>
  );
}
